import type { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { useWindowKeydown } from '@elfsight/lib-utils';
import { Popout } from '@elfsight/widget-modal';
import { scrollbarColoredHoverMixin } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { checkBackground } from '../../../utils/check-background';

type DropdownProps = PropsWithChildren<{
  anchorElement: Element | null;
  open: boolean;
  onCloseRequest(): void;
}>;

export function Dropdown({
  children,
  anchorElement,
  open = false,
  onCloseRequest
}: DropdownProps) {
  const {
    embedType,
    position: { position },
    ...settings
  } = useSettings();

  const hasBackground = checkBackground(settings);

  function getAlignment() {
    if (embedType === 'floating' && position.includes('right')) {
      return 'right';
    }

    return 'left';
  }

  function offsetLeft() {
    if (!hasBackground) {
      return -4;
    }

    if (getAlignment() === 'right') {
      return 12;
    }

    return -12;
  }

  const direction =
    embedType === 'floating' && position.includes('bottom')
      ? 'upward'
      : 'downward';

  useWindowKeydown('Escape', onCloseRequest);

  const portalContainerElement =
    embedType === 'floating'
      ? anchorElement?.parentElement?.parentElement?.parentElement
      : anchorElement?.parentElement?.parentElement;

  return (
    <Popout
      portalContainerElement={
        portalContainerElement ? portalContainerElement : undefined
      }
      anchorElement={anchorElement}
      show={open}
      offsetTop={hasBackground ? 12 : 4}
      offsetLeft={offsetLeft()}
      alignment={getAlignment()}
      direction={direction}
      onCloseRequest={onCloseRequest}
      closeOnOutsideClick
    >
      <Container role="listbox" aria-label="select language">
        {children}
      </Container>
    </Popout>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0px;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  background: #fff;
  border-radius: 8px;
  box-sizing: border-box;
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.2);

  ${scrollbarColoredHoverMixin()}
`;
